import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { supabase, supabaseServiceRole } from '../supabaseClient';
import './AnimatedHelper.css';
import animation1 from '../assets/animations/animation1.png';
import animation2 from '../assets/animations/animation2.png';
import animation3 from '../assets/animations/animation3.png';
import animation4 from '../assets/animations/animation4.png';
import animation41 from '../assets/animations/animation4.1.png';
import animation42 from '../assets/animations/animation4.2.png';
import animation5mm from '../assets/animations/animation5mm.png';
import animation5mm6 from '../assets/animations/animation5mm6.png';
import animation6 from '../assets/animations/animation6.png';
import animation7 from '../assets/animations/animation7.png';
import animation8 from '../assets/animations/animation8.png';
import animation9 from '../assets/animations/animation9.png';
import animation10 from '../assets/animations/animation10.png';
import buttonImage from '../buttonimage.webp'; // Add this import

const AnimatedHelper = ({
  isActive: initialIsActive,
  initialQuestionId,
  onButtonClick,
  onClose,
  parentComponent,
  setCurrentPage,
  isMobile: propIsMobile
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [minimized, setMinimized] = useState(false);
  const [fullyMinimized, setFullyMinimized] = useState(false);
  const [position, setPosition] = useState(() => {
    const widthOffset = window.innerWidth > 768 ? 300 : 380;
    return { x: Math.max(30, window.innerWidth / 2 - 280 - widthOffset), y: window.innerHeight / 2 - 190 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [lastTap, setLastTap] = useState(0);
  const helperRef = useRef(null);
  const animationTimer = useRef(null);
  const startPos = useRef({ x: 0, y: 0 });
  const lastButtonPos = useRef({ x: 0, y: 0 });
  const [question, setQuestion] = useState(null);
  const [buttons, setButtons] = useState([]);
  const hasPlayedAnimation = useRef(false);
  const fetchRef = useRef(null);
  // `fetchRefLocal` alias removed because it was unused; `fetchRef` is used directly below.

  // Responsive device detection for anchors
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fix device detection breakpoints for mobile/tablet/desktop
  const isMobile = typeof propIsMobile === 'boolean' ? propIsMobile : windowWidth <= 767;
  const isTablet = !isMobile && windowWidth >= 768 && windowWidth <= 1024;
  // Optionally, for large tablets only:
  // const isTablet = !isMobile && windowWidth >= 900 && windowWidth <= 1200;

  const animationImages = useMemo(() => [animation1, animation2, animation3, animation4, animation41, animation42, animation5mm], []);
  const animationImagesMobile = useMemo(() => [animation1, animation2, animation3, animation4, animation41, animation42, animation5mm6], []);
  const animationImagesToUse = isMobile ? animationImagesMobile : animationImages;
  const endOfSessionImages = useMemo(() => [animation6, animation7, animation8, animation9, animation10], []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    console.log('Animation starting, hasPlayedAnimation:', hasPlayedAnimation.current, 'parentComponent:', parentComponent, 'initialQuestionId:', initialQuestionId, 'isActive:', initialIsActive);

    if (!initialIsActive) {
      setCurrentImageIndex(0);
      setMinimized(false);
      setFullyMinimized(false);
      setQuestion(null);
      setButtons([]);
      hasPlayedAnimation.current = false;
      return;
    }

    if (parentComponent === 'EndofSession') {
      setCurrentImageIndex(0);
      setMinimized(false);
      setFullyMinimized(false);
      setQuestion(null);
      setButtons([]);
      if (animationTimer.current) clearInterval(animationTimer.current);
      let index = 0;
      animationTimer.current = setInterval(() => {
        setCurrentImageIndex(prev => {
          if (prev < endOfSessionImages.length - 1) {
            return prev + 1;
          }
          return prev;
        });
        index++;
        if (index >= endOfSessionImages.length) {
          clearInterval(animationTimer.current);
          animationTimer.current = null;
          if (onClose) onClose();
        }
      }, 1000);
      return;
    }

    if (initialQuestionId) {
      setQuestion(null);
      setButtons([]);
      setCurrentImageIndex(0); // Always start animation from the first image

      if (animationTimer.current) {
        clearInterval(animationTimer.current);
        animationTimer.current = null;
      }

      fetchHelperData(initialQuestionId).then(data => {
        if (data) {
          setQuestion(data.question);
          setButtons(data.options);
          let index = 0;
          setCurrentImageIndex(0); // Ensure animation starts at the first image

          // --- Double the speed again: 125ms per frame ---
          animationTimer.current = setInterval(() => {
            setCurrentImageIndex(prev => {
              if (prev < animationImagesToUse.length - 1) {
                return prev + 1;
              }
              return prev;
            });
            index++;
            if (index >= animationImagesToUse.length) {
              clearInterval(animationTimer.current);
              animationTimer.current = null;
              hasPlayedAnimation.current = true;
            }
          }, 125); // <-- 125ms per frame (8x original speed)
        }
      }).catch(err => console.error('Fetch error:', err));
    } else {
      setCurrentImageIndex(animationImagesToUse.length - 1);
    }

    return () => {
      if (animationTimer.current) {
        clearInterval(animationTimer.current);
        animationTimer.current = null;
      }
    };
  }, [initialIsActive, initialQuestionId, parentComponent, animationImagesToUse.length, onClose, endOfSessionImages.length]);

  const fetchHelperData = async (questionId) => {
    if (fetchRef.current === questionId) return null;
    fetchRef.current = questionId;
    try {
      console.log('Fetching helper data for question ID:', questionId);
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        console.error('No session or token:', sessionError?.message);
        return null;
      }
      const { data: fetchedQuestion, error: questionError } = await supabase
        .from('questions3')
        .select('id, text, component, is_first')
        .eq('id', questionId)
        .single();
      if (questionError) {
        console.error('Error fetching question3:', questionError.message);
        return null;
      }
      console.log('Fetched helper question:', fetchedQuestion);
      const { data: answers, error: answersError } = await supabaseServiceRole
        .from('answers3')
        .select('id, text, component, next_question3_id, sequence, visited')
        .eq('question3_id', questionId);
      if (answersError) {
        console.error('Error fetching answers3:', answersError.message);
        return null;
      }
      // --- Sort answers by sequence ascending ---
      const sortedAnswers = (answers || []).slice().sort((a, b) => (a.sequence ?? 999) - (b.sequence ?? 999));
      console.log('Fetched helper options:', sortedAnswers);
      const options = sortedAnswers.map(a => ({
        id: a.id,
        label: a.text,
        path: a.component || '/',
        nextQuestionId: a.next_question3_id,
        component: a.component
      }));
      return { question: fetchedQuestion, options };
    } catch (err) {
      console.error('Unexpected error fetching helper data:', err.message);
      return null;
    } finally {
      fetchRef.current = null;
    }
  };

  const handleMinimize = () => {
    if (currentImageIndex === animationImagesToUse.length - 1) {
      if (!minimized) {
        setMinimized(true);
        setPosition(prev => ({
          x: Math.max(10, window.innerWidth > 768 ? document.documentElement.clientWidth - 320 - 10 - 200 - 100 : document.documentElement.clientWidth - 320 - 10 - 200 - 100 - 80),
          y: 10
        }));
      } else if (fullyMinimized) {
        setFullyMinimized(false);
        setMinimized(false);
        setCurrentImageIndex(0);
        let index = 0;
        const timer = setInterval(() => {
          console.log('Restoring animation to index:', index);
          if (index < animationImagesToUse.length - 1) {
            setCurrentImageIndex(prev => prev + 1);
            index++;
          } else {
            console.log('Restore complete');
            clearInterval(timer);
            if (initialQuestionId) fetchHelperData(initialQuestionId);
          }
        }, 1000);
      } else {
        setFullyMinimized(true);
      }
    }
  };

  const handleRestore = () => {
    if (fullyMinimized || minimized) {
      setFullyMinimized(false);
      setMinimized(false);
      setCurrentImageIndex(0);
      const widthOffset = window.innerWidth > 768 ? 300 : 380;
      setPosition({ x: Math.max(30, window.innerWidth / 2 - 280 - widthOffset), y: window.innerHeight / 2 - 190 });
      let index = 0;
      const timer = setInterval(() => {
        console.log('Restoring animation to index:', index);
        if (index < animationImagesToUse.length - 1) {
          setCurrentImageIndex(prev => prev + 1);
          index++;
        } else {
          console.log('Restore complete');
          clearInterval(timer);
          if (initialQuestionId) fetchHelperData(initialQuestionId);
        }
      }, 1000);
    }
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      console.error('No authenticated user:', userError?.message || 'User object is null');
      return;
    }
    const { data: profile } = await supabase
      .schema('api')
      .from('user_profiles')
      .select('membership_type')
      .eq('id', user.id)
      .single();
    if (profile.membership_type === 'Free') {
      alert('You have completed your free monthly session. Please upgrade to a Monthly or Yearly Subscription to utilize this function and have access to unlimited sessions per month. Your free subscription offers one free session per month and will be reset in 30 days.');
      return;
    }
    const progress = {
      user_id: user.id,
      current_question_id: initialQuestionId,
      current_component: parentComponent || 'HealingFormulas',
      animated_helper: 'yes' // <-- always sets to 'yes' here
    };
    const { error } = await supabase
      .from('user_progress')
      .upsert(progress, { onConflict: ['user_id'] });
    if (error) {
      console.error('Error saving progress:', error.message);
      return;
    }
    console.log('Progress saved successfully:', progress);
    alert('Progress saved successfully!');
  };

  const startDragging = (e) => {
    if (!fullyMinimized && currentImageIndex === animationImagesToUse.length - 1) {
      if (e.type === 'mousedown' || (e.type === 'touchstart' && isDoubleTap())) {
        setIsDragging(true);
        startPos.current = e.type === 'mousedown'
          ? { x: e.clientX - position.x, y: e.clientY - position.y }
          : { x: e.touches[0].clientX - position.x, y: e.touches[0].clientY - position.y };
      }
    }
  };

  const stopDragging = () => setIsDragging(false);

  const handleDrag = (e) => {
    if (isDragging && helperRef.current && currentImageIndex === animationImagesToUse.length - 1) {
      const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
      const clientY = e.type === 'mousemove' ? e.clientY : e.touches[0].clientY;
      const newX = clientX - startPos.current.x;
      const newY = clientY - startPos.current.y;
      if (Math.abs(newX - position.x) > 1 || Math.abs(newY - position.y) > 1) {
        setPosition({ x: newX, y: newY });
      }
    }
  };

  const isDoubleTap = () => {
    const now = new Date().getTime();
    const timeDiff = now - lastTap;
    const tapValid = timeDiff < 300;
    setLastTap(now);
    return tapValid;
  };

  const handleAnswerClick = async (path, nextQuestionId, component, event) => {
    console.log('Answer clicked:', { path, nextQuestionId, component });
    if (event) {
      const buttonRect = event.currentTarget.getBoundingClientRect();
      const helperWidth = helperRef.current ? helperRef.current.offsetWidth : 560;
      const helperHeight = helperRef.current ? helperRef.current.offsetHeight : 380;
      lastButtonPos.current = {
        x: buttonRect.left + (buttonRect.width / 2) - (helperWidth / 2),
        y: buttonRect.top + (buttonRect.height / 2) - (helperHeight / 2)
      };
      setPosition(lastButtonPos.current);
    }

    if (nextQuestionId) {
      const data = await fetchHelperData(nextQuestionId);
      if (data) {
        console.log('Setting new question and buttons:', { question: data.question, options: data.options });
        setQuestion(data.question);
        setButtons(data.options);
      }
    }
    if (component || (path && !nextQuestionId)) {
      onButtonClick({ path: path || component, nextQuestionId: nextQuestionId || initialQuestionId });
      if (setCurrentPage) setCurrentPage(component || path);
    } else {
      onButtonClick({ path, nextQuestionId });
    }
  };

  useEffect(() => {
    if (lastButtonPos.current.x !== 0 || lastButtonPos.current.y !== 0) {
      setPosition(lastButtonPos.current);
    }
  }, [parentComponent]);

  // Use strict, non-overlapping breakpoints
  // (isMobile and isTablet already declared above)

  // All anchor widths/positions are in percent, relative to the image/container
  const anchors = {
    question: isMobile
      ? { top: 8, left: 65, width: 60, transform: 'translate(-50%, 0)' }
      : isTablet
        ? { top: 12, left: 51, width: 30, transform: 'translate(-50%, 0)' }
        : { top: 5, left: 60, width: 41, transform: 'translate(-50%, 0)' },
    buttons: isMobile
      ? { top: 48, left: 65, width: 40, transform: 'translate(-50%, 0)' }
      : isTablet
        ? { top: 56, left: 60, width: 35, transform: 'translate(-50%, 0)' }
        : { top: 49, left: 56, width: 20, transform: 'translate(-50%, 0)' },
    save: isMobile
      ? { top: 22, left: 19, width: 10, transform: 'translate(-50%, 0)' }
      : isTablet
        ? { top: 20, left: 30, width: 8, transform: 'translate(-50%, 0)' }
        : { top: 20, left: 31, width: 7, transform: 'translate(-50%, 0)' },
    controls: isMobile
      ? { top: 2, left: 20 }
      : isTablet
        ? { top: 0, left: 29 }
        : { top: 1, left: 30 },
    minimized_question: isMobile
      ? { top: 5, left: 60, width: 40, transform: 'translate(-50%, 0)' }
      : isTablet
        ? { top: 8, left: 60, width: 36, transform: 'translate(-50%, 0)' }
        : { top: 5, left: 60, width: 38, transform: 'translate(-50%, 0)' },
    minimized_buttons: isMobile
      ? { top: 40, left: 53, width: 20, transform: 'translate(-50%, 0)' }
      : isTablet
        ? { top: 60, left: 50, width: 20, transform: 'translate(-50%, 0)' }
        : { top: 40, left: 58, width: 25, transform: 'translate(-50%, 0)' },
    minimized_save: isMobile
      ? { top: 17, left: 27, width: 7, transform: 'translate(-50%, 0)' }
      : isTablet
        ? { top: 15, left: 25, width: 6, transform: 'translate(-50%, 0)' }
        : { top: 15, left: 27, width: 5, transform: 'translate(-50%, 0)' },
    minimized_controls: isMobile
      ? { top: -1, left: 25 }
      : isTablet
        ? { top: 1, left: 25 }
        : { top: -2, left: 25 }
  };

  const getImageAnchorStyle = (anchorType) => {
    const anchor = minimized && !fullyMinimized && anchors[`minimized_${anchorType}`]
      ? anchors[`minimized_${anchorType}`]
      : anchors[anchorType];
    return {
      position: 'absolute',
      top: anchor.top !== undefined ? `${anchor.top}%` : undefined,
      left: anchor.left !== undefined ? `${anchor.left}%` : undefined,
      width: anchor.width !== undefined ? `${anchor.width}%` : undefined,
      height: anchor.height !== undefined ? `${anchor.height}%` : undefined,
      transform: anchor.transform || undefined,
      ...anchor.extra
    };
  };

  if (!initialIsActive) {
    return null; // Don't render if not active, but don't call onClose
  }

  if (fullyMinimized) {
    console.log('Rendering fully minimized state, position:', position);
    return (
      <div
        className="helper-popup minimized-bar"
        style={{
          position: 'absolute',
          top: position.y + 10,
          left: position.x + 10,
          zIndex: 9999,
          background: 'transparent',
          border: 'none',
          borderRadius: 0,
          padding: 0,
          boxShadow: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          minWidth: 0
        }}
      >
        <span
          style={{
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '18px',
            background: 'white',
            borderRadius: '4px',
            padding: '2px 8px',
            marginRight: '4px'
          }}
          onClick={handleRestore}
        >□</span>
        <span
          style={{
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '18px',
            background: 'white',
            borderRadius: '4px',
            padding: '2px 8px'
          }}
          onClick={onClose}
        >X</span>
      </div>
    );
  }

  if (parentComponent === 'EndofSession') {
    return (
      <motion.div
        ref={helperRef}
        className="helper-popup"
        style={{
          top: position.y,
          left: position.x,
          position: 'absolute'
        }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="helper-content" style={{
          position: 'relative',
          width: window.innerWidth > 768 ? '560px' : '90vw',
          height: '380px'
        }}>
          <img
            src={endOfSessionImages[Math.max(0, Math.min(currentImageIndex, endOfSessionImages.length - 1))]}
            alt="End of Session Animation"
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              objectFit: 'contain'
            }}
          />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={helperRef}
      className={`helper-popup ${minimized ? 'minimized' : ''}`}
      style={{
        top: position.y,
        left: position.x,
        position: 'absolute'
      }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: minimized ? 0.75 : 1 }}
      transition={{ duration: 0.3 }}
      onMouseDown={startDragging}
      onMouseMove={handleDrag}
      onMouseUp={stopDragging}
      onMouseLeave={stopDragging}
      onTouchStart={startDragging}
      onTouchMove={handleDrag}
      onTouchEnd={stopDragging}
    >
      <div className="helper-content" style={{
        position: 'relative',
        width: window.innerWidth > 768 ? (minimized ? '320px' : '560px') : (minimized ? '300px' : '90vw'),
        height: minimized ? '220px' : '380px'
      }}>
        <img
          src={
            minimized && currentImageIndex === animationImagesToUse.length - 1
              ? animation5mm
              : animationImagesToUse[Math.max(0, Math.min(currentImageIndex, animationImagesToUse.length - 1))]
          }
          alt="Animation"
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            objectFit: 'contain'
          }}
        />
        {currentImageIndex === animationImagesToUse.length - 1 && question && (
          <div
            className="question-overlay"
            style={{
              ...getImageAnchorStyle('question'),
              padding: 0,
              margin: 0,
              lineHeight: '1.1',
              fontWeight: 'bold',
              fontFamily: 'Times New Roman, Times, serif',
              fontSize: minimized ? (isMobile ? '9px' : '10px') : (isMobile ? '13px' : '15px')
            }}
          >
            {question.text}
          </div>
        )}
        {currentImageIndex === animationImagesToUse.length - 1 && buttons.length > 0 ? (
          <div
            className={`button-container ${minimized ? 'minimized' : ''}`}
            style={getImageAnchorStyle('buttons')}
          >
            {buttons.map((button, index) => (
              <button
                key={button.id || index}
                onClick={(e) => handleAnswerClick(button.path, button.nextQuestionId, button.component, e)}
                style={{
                  borderRadius: '5px',
                  boxShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                  padding: minimized ? '0' : '0 5px',
                  margin: '0 0 5px 0',
                  fontSize: minimized ? '10px' : '14px',
                  width: 'auto',
                  height: 'auto',
                  whiteSpace: 'nowrap',
                  lineHeight: minimized ? '10px' : 'normal',
                  backgroundImage: `url(${buttonImage})`,
                  backgroundSize: 'cover',
                  color: 'white',
                  border: 'none',
                  fontFamily: 'Times New Roman, Times, serif',
                  fontWeight: 'bold', // Make answer buttons bold
                }}
              >
                {button.label}
              </button>
            ))}
          </div>
        ) : currentImageIndex === animationImagesToUse.length - 1 && question && (
          <div
            className="no-options-message"
            style={getImageAnchorStyle('buttons')}
          >
            No options available for this question.
          </div>
        )}
        {currentImageIndex === animationImagesToUse.length - 1 && (
          <div className={`save-button ${minimized ? 'minimized' : ''}`} style={getImageAnchorStyle('save')}>
            <button
              onClick={handleSave}
              style={{
                width: 'auto',
                height: 'auto',
                padding: '1px 1px',
                fontSize: minimized ? '10px' : '14px',
                borderRadius: '5px',
                backgroundImage: `url(${buttonImage})`,
                backgroundSize: 'cover',
                color: 'white',
                border: 'none',
                boxShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                fontFamily: 'Times New Roman, Times, serif', // Save button font
                fontWeight: 'bold', // Save button bold
              }}
            >Save</button>
          </div>
        )}
        <div className="controls" style={getImageAnchorStyle('controls')}>
          <span onClick={handleMinimize}>−</span>
          <span onClick={onClose}>X</span>
          {minimized && (
            <span onClick={handleRestore}>□</span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default AnimatedHelper;